"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.composeFormModel = composeFormModel;
const immer_1 = require("immer");
const types_1 = require("../shared/types");
function* iterateModels(model, modelsByRef, composeIndices, reporter) {
    if (!model.composables?.length) {
        return;
    }
    for (const [index, composable] of model.composables.entries()) {
        const indices = [...composeIndices, index];
        if (!composable.ref || !modelsByRef[composable.ref]) {
            if (reporter) {
                reporter(`Could not find composable for path "${composable.path}"`, {
                    indices,
                });
                continue;
            }
            else {
                throw new Error(`Could not find a composable for path "${composable.path}"`);
            }
        }
        const modelOrError = modelsByRef[composable.ref];
        if (modelOrError instanceof Error) {
            if (reporter) {
                reporter(modelOrError.message, {
                    indices,
                });
                continue;
            }
            else {
                throw modelOrError;
            }
        }
        yield* iterateModels(modelOrError, modelsByRef, indices, reporter);
        yield [composable, modelOrError, indices];
    }
}
function composeFormModel(model, modelsByRef, reporter) {
    const sourceMap = {
        refToIndices: {},
        termsToRef: {},
        iterablesToRef: {},
        metaToRef: {},
        iterableTermsToRef: {},
    };
    const composedModel = (0, immer_1.produce)(model, draft => {
        // First, move all the composable defaults and parameters from the model
        // to the delayed compose terms objects
        const defaultsToCompose = {
            terms: {},
            iterables: {},
        };
        const parametersToCompose = {
            terms: {},
            iterables: {},
        };
        for (const [termName, term] of Object.entries(draft.terms)) {
            if (term.compose === "defaults") {
                defaultsToCompose.terms[termName] = { term };
                delete draft.terms[termName];
            }
            else if (term.compose === "parameters") {
                parametersToCompose.terms[termName] = { term };
                delete draft.terms[termName];
            }
        }
        for (const [iterableName, iterable] of Object.entries(draft.iterables)) {
            if (iterable.compose === "integrate") {
                for (const [termName, term] of Object.entries(iterable.terms)) {
                    if (term.compose === "defaults") {
                        if (!defaultsToCompose.iterables[iterableName]) {
                            defaultsToCompose.iterables[iterableName] = {};
                        }
                        defaultsToCompose.iterables[iterableName][termName] = { term };
                        delete draft.iterables[iterableName].terms[termName];
                    }
                    else if (term.compose === "parameters") {
                        if (!parametersToCompose.iterables[iterableName]) {
                            parametersToCompose.iterables[iterableName] = {};
                        }
                        parametersToCompose.iterables[iterableName][termName] = { term };
                        delete draft.iterables[iterableName].terms[termName];
                    }
                }
            }
        }
        for (const [composable, composableModel, indices] of iterateModels(model, modelsByRef, [], reporter)) {
            if (!composable.ref) {
                throw new Error(`The composable "${composable.path}" is missing a ref`);
            }
            // Ignore some files that have already been composed
            if (sourceMap.refToIndices[composable.ref]) {
                continue;
            }
            const ref = composable.ref;
            sourceMap.refToIndices[ref] = indices;
            let applyCascadingKey;
            if (composableModel.key) {
                applyCascadingKey = composableModel.key;
            }
            else if (draft.key) {
                // Apply the default key to the composed terms
                applyCascadingKey = {
                    type: "syntax",
                    value: "this.term.name",
                };
            }
            for (const [termName, term] of Object.entries(composableModel.terms)) {
                if (term.compose === "defaults") {
                    if (defaultsToCompose.terms[termName]) {
                        const msg = `The term "${termName}" from "${composable.path}" already has defaults.`;
                        if (reporter) {
                            reporter(msg, {
                                indices,
                                ref,
                                termName,
                            });
                            continue;
                        }
                        throw new Error(msg);
                    }
                    defaultsToCompose.terms[termName] = {
                        term,
                        indices,
                        ref,
                    };
                    continue;
                }
                if (term.compose === "parameters") {
                    if (parametersToCompose.terms[termName]) {
                        const msg = `The term "${termName}" from "${composable.path}" already has parameters.`;
                        if (reporter) {
                            reporter(msg, {
                                indices,
                                ref,
                                termName,
                            });
                            continue;
                        }
                        throw new Error(msg);
                    }
                    parametersToCompose.terms[termName] = {
                        term,
                        indices,
                        ref,
                    };
                    continue;
                }
                if (!draft.terms[termName] ||
                    draft.terms[termName].compose === "replace") {
                    if (applyCascadingKey && !term.key) {
                        term.key = applyCascadingKey;
                    }
                    draft.terms[termName] = term;
                    sourceMap.termsToRef[termName] = ref;
                    continue;
                }
                if (term.compose === "replace") {
                    // This term was meant to be replaced during composition
                    continue;
                }
                const msg = `The term "${termName}" from "${composable.path}" cannot be added because it was already defined`;
                if (reporter) {
                    reporter(msg, {
                        indices,
                        ref,
                        termName,
                    });
                    continue;
                }
                throw new Error(msg);
            }
            for (const [iterableName, iterable] of Object.entries(composableModel.iterables)) {
                if (!draft.iterables[iterableName] ||
                    draft.iterables[iterableName].compose === "replace") {
                    draft.iterables[iterableName] = iterable;
                    sourceMap.iterablesToRef[iterableName] = ref;
                    continue;
                }
                if (iterable.compose === "integrate" ||
                    draft.iterables[iterableName].compose === "integrate") {
                    if (iterable.compose !== "integrate") {
                        draft.iterables[iterableName].compose = iterable.compose;
                        draft.iterables[iterableName].count = iterable.count;
                        draft.iterables[iterableName].max = iterable.max;
                    }
                    let applyCascadingIterableKey;
                    if (iterable.key) {
                        applyCascadingIterableKey = iterable.key;
                    }
                    else if (draft.iterables[iterableName].key) {
                        // Apply the default key to the integrated iterable terms
                        applyCascadingIterableKey = {
                            type: "syntax",
                            value: "fn.text.concat(this.term.name, this.position)",
                        };
                    }
                    for (const [termName, term] of Object.entries(iterable.terms)) {
                        if (term.compose === "defaults") {
                            if (!defaultsToCompose.iterables[iterableName]) {
                                defaultsToCompose.iterables[iterableName] = {};
                            }
                            if (defaultsToCompose.iterables[iterableName][termName]) {
                                const msg = `The term "${iterableName}/${termName}" from "${composable.path}" already has defaults.`;
                                if (reporter) {
                                    reporter(msg, {
                                        indices,
                                        ref,
                                        termName,
                                        iterableName,
                                    });
                                    continue;
                                }
                                throw new Error(msg);
                            }
                            defaultsToCompose.iterables[iterableName][termName] = {
                                term,
                                indices,
                                ref,
                            };
                            continue;
                        }
                        if (term.compose === "parameters") {
                            if (!parametersToCompose.iterables[iterableName]) {
                                parametersToCompose.iterables[iterableName] = {};
                            }
                            if (parametersToCompose.iterables[iterableName][termName]) {
                                const msg = `The term "${iterableName}/${termName}" from "${composable.path}" already has parameters.`;
                                if (reporter) {
                                    reporter(msg, {
                                        indices,
                                        ref,
                                        termName,
                                        iterableName,
                                    });
                                    continue;
                                }
                                throw new Error(msg);
                            }
                            parametersToCompose.iterables[iterableName][termName] = {
                                term,
                                indices,
                                ref,
                            };
                            continue;
                        }
                        if (!draft.iterables[iterableName].terms[termName] ||
                            draft.iterables[iterableName].terms[termName].compose ===
                                "replace") {
                            if (applyCascadingIterableKey && !term.key) {
                                term.key = applyCascadingIterableKey;
                            }
                            draft.iterables[iterableName].terms[termName] = term;
                            if (!sourceMap.iterableTermsToRef[iterableName]) {
                                sourceMap.iterableTermsToRef[iterableName] = {};
                            }
                            sourceMap.iterableTermsToRef[iterableName][termName] = ref;
                            continue;
                        }
                        if (term.compose === "replace") {
                            // This term was meant to be composed
                            continue;
                        }
                        const msg = `The iterable term "${iterableName}/${termName}" from "${composable.path}" cannot be added because it was already defined`;
                        if (reporter) {
                            reporter(msg, {
                                indices,
                                ref,
                                termName,
                                iterableName,
                            });
                            continue;
                        }
                        throw new Error(msg);
                    }
                    continue;
                }
                if (iterable.compose === "replace") {
                    // This term was meant to be composed
                    continue;
                }
                const msg = `The iterable "${iterableName}" from "${composable.path}" cannot be added because it was already defined`;
                if (reporter) {
                    reporter(msg, {
                        indices,
                        ref,
                        iterableName,
                    });
                    continue;
                }
                throw new Error(msg);
            }
            for (const [metaName, meta] of Object.entries(composableModel.meta)) {
                if (!draft.meta[metaName] ||
                    draft.meta[types_1.metaComposeDirectives]?.[metaName] === "replace") {
                    draft.meta[metaName] = meta;
                    sourceMap.metaToRef[metaName] = ref;
                    // Replace the meta compose directive if necessary
                    if (composableModel.meta[types_1.metaComposeDirectives]?.[metaName]) {
                        if (!draft.meta[types_1.metaComposeDirectives]) {
                            draft.meta[types_1.metaComposeDirectives] = {};
                        }
                        draft.meta[types_1.metaComposeDirectives][metaName] =
                            composableModel.meta[types_1.metaComposeDirectives][metaName];
                    }
                    else if (draft.meta[types_1.metaComposeDirectives]?.[metaName]) {
                        delete draft.meta[types_1.metaComposeDirectives][metaName];
                    }
                    continue;
                }
                if (composableModel.meta[types_1.metaComposeDirectives]?.[metaName] === "replace") {
                    // This meta was meant to be replaced during composition
                    continue;
                }
                const msg = `The meta item "${metaName}" from "${composable.path}" cannot be added because it was already defined`;
                if (reporter) {
                    reporter(msg, {
                        indices,
                        ref,
                        metaName,
                    });
                    continue;
                }
                throw new Error(msg);
            }
        }
        // Apply the delayed compose terms
        for (const [termName, termWithRef] of Object.entries(defaultsToCompose.terms)) {
            if (!draft.terms[termName]) {
                draft.terms[termName] = termWithRef.term;
                if (termWithRef.ref) {
                    sourceMap.termsToRef[termName] = termWithRef.ref;
                }
                continue;
            }
            if (draft.terms[termName].compose === "replace") {
                draft.terms[termName] = termWithRef.term;
                continue;
            }
            useTermDefaults(draft.terms[termName], termWithRef.term);
        }
        for (const [iterableName, iterableTerms] of Object.entries(defaultsToCompose.iterables)) {
            for (const [termName, termWithRef] of Object.entries(iterableTerms)) {
                if (!draft.iterables[iterableName].terms[termName]) {
                    draft.iterables[iterableName].terms[termName] = termWithRef.term;
                    if (termWithRef.ref) {
                        if (!sourceMap.iterableTermsToRef[iterableName]) {
                            sourceMap.iterableTermsToRef[iterableName] = {};
                        }
                        sourceMap.iterableTermsToRef[iterableName][termName] =
                            termWithRef.ref;
                    }
                    continue;
                }
                if (draft.iterables[iterableName].terms[termName].compose === "replace") {
                    draft.iterables[iterableName].terms[termName] = termWithRef.term;
                    continue;
                }
                useTermDefaults(draft.iterables[iterableName].terms[termName], termWithRef.term);
            }
        }
        for (const [termName, termWithRef] of Object.entries(parametersToCompose.terms)) {
            if (!draft.terms[termName]) {
                draft.terms[termName] = termWithRef.term;
                if (termWithRef.ref) {
                    sourceMap.termsToRef[termName] = termWithRef.ref;
                }
                continue;
            }
            if (draft.terms[termName].compose === "replace") {
                draft.terms[termName] = termWithRef.term;
                continue;
            }
            const outcome = useTermParameters(draft.terms[termName], termWithRef.term);
            if (outcome.type === "error") {
                if (reporter) {
                    reporter(outcome.message, {
                        termName,
                        indices: termWithRef.indices || [],
                        ref: termWithRef.ref,
                    });
                    continue;
                }
                else {
                    throw new Error(outcome.message);
                }
            }
        }
        for (const [iterableName, iterableTerms] of Object.entries(parametersToCompose.iterables)) {
            for (const [termName, termWithRef] of Object.entries(iterableTerms)) {
                if (!draft.iterables[iterableName].terms[termName]) {
                    draft.iterables[iterableName].terms[termName] = termWithRef.term;
                    if (termWithRef.ref) {
                        if (!sourceMap.iterableTermsToRef[iterableName]) {
                            sourceMap.iterableTermsToRef[iterableName] = {};
                        }
                        sourceMap.iterableTermsToRef[iterableName][termName] =
                            termWithRef.ref;
                    }
                    continue;
                }
                if (draft.iterables[iterableName].terms[termName].compose === "replace") {
                    draft.iterables[iterableName].terms[termName] = termWithRef.term;
                    continue;
                }
                const outcome = useTermParameters(draft.iterables[iterableName].terms[termName], termWithRef.term);
                if (outcome.type === "error") {
                    if (reporter) {
                        reporter(outcome.message, {
                            termName,
                            indices: termWithRef.indices || [],
                            ref: termWithRef.ref,
                        });
                        continue;
                    }
                    else {
                        throw new Error(outcome.message);
                    }
                }
            }
        }
        // Move the meta compose directives to the source map
        if (draft.meta[types_1.metaComposeDirectives]) {
            sourceMap.metaComposeDirectives = draft.meta[types_1.metaComposeDirectives];
            delete draft.meta[types_1.metaComposeDirectives];
        }
    });
    return [composedModel, sourceMap];
}
function useTermDefaults(existingTerm, composingTerm) {
    if (existingTerm.key === undefined && composingTerm.key !== undefined) {
        existingTerm.key = composingTerm.key;
    }
    if (existingTerm.valueType === undefined &&
        composingTerm.valueType !== undefined) {
        existingTerm.valueType = composingTerm.valueType;
    }
    if (existingTerm.format === undefined && composingTerm.format !== undefined) {
        existingTerm.format = composingTerm.format;
    }
    if (existingTerm.use === undefined && composingTerm.use !== undefined) {
        existingTerm.use = composingTerm.use;
    }
    if (existingTerm.sync === undefined && composingTerm.sync !== undefined) {
        existingTerm.sync = composingTerm.sync;
    }
    if (existingTerm.default === undefined &&
        composingTerm.default !== undefined) {
        existingTerm.default = composingTerm.default;
    }
    if (existingTerm.value === undefined && composingTerm.value !== undefined) {
        existingTerm.value = composingTerm.value;
    }
    if (existingTerm.separator === undefined &&
        composingTerm.separator !== undefined) {
        existingTerm.separator = composingTerm.separator;
    }
    if (existingTerm.label === undefined && composingTerm.label !== undefined) {
        existingTerm.label = composingTerm.label;
    }
    if (existingTerm.enums === undefined &&
        existingTerm.enumsFn === undefined &&
        (composingTerm.enums !== undefined || composingTerm.enumsFn !== undefined)) {
        existingTerm.enums = composingTerm.enums;
        existingTerm.enumsFn = composingTerm.enumsFn;
    }
    if (existingTerm.calculate === undefined &&
        composingTerm.calculate !== undefined) {
        existingTerm.calculate = composingTerm.calculate;
    }
    if (existingTerm.relevant === undefined &&
        composingTerm.relevant !== undefined) {
        existingTerm.relevant = composingTerm.relevant;
    }
    if (existingTerm.required === undefined &&
        composingTerm.required !== undefined) {
        existingTerm.required = composingTerm.required;
    }
    if (existingTerm.readonly === undefined &&
        composingTerm.readonly !== undefined) {
        existingTerm.readonly = composingTerm.readonly;
    }
    if (existingTerm.validationList === undefined &&
        composingTerm.validationList !== undefined) {
        existingTerm.validationList = composingTerm.validationList;
    }
    if (existingTerm.events === undefined && composingTerm.events !== undefined) {
        existingTerm.events = composingTerm.events;
    }
    if (composingTerm.meta !== undefined) {
        if (existingTerm.meta === undefined) {
            existingTerm.meta = composingTerm.meta;
        }
        else if (existingTerm.meta !== undefined) {
            existingTerm.meta = {
                ...composingTerm.meta,
                ...existingTerm.meta,
            };
        }
    }
}
function useTermParameters(existingTerm, composingTerm) {
    if (composingTerm.key !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have a "key" with compose parameters`,
        };
    }
    if (composingTerm.valueType !== undefined ||
        composingTerm.format !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have a "type" with compose parameters`,
        };
    }
    if (composingTerm.use !== undefined) {
        existingTerm.use = composingTerm.use;
    }
    if (composingTerm.sync !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have a "sync" with compose parameters`,
        };
    }
    if (composingTerm.default !== undefined) {
        existingTerm.default = composingTerm.default;
    }
    if (composingTerm.value !== undefined) {
        existingTerm.value = composingTerm.value;
    }
    if (composingTerm.separator !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have a "separator" with compose parameters`,
        };
    }
    if (composingTerm.label !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have a "label" with compose parameters`,
        };
    }
    if (composingTerm.enums !== undefined ||
        composingTerm.enumsFn !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have "enums" with compose parameters`,
        };
    }
    if (composingTerm.calculate !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have a "calculate" with compose parameters`,
        };
    }
    if (composingTerm.relevant !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have a "relevant" with compose parameters`,
        };
    }
    if (composingTerm.required !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have a "required" with compose parameters`,
        };
    }
    if (composingTerm.readonly !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have a "readonly" with compose parameters`,
        };
    }
    if (composingTerm.validationList !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have "validation" with compose parameters`,
        };
    }
    if (composingTerm.events !== undefined) {
        return {
            type: "error",
            message: `The term "${existingTerm.name}" cannot have "events" with compose parameters`,
        };
    }
    if (composingTerm.meta !== undefined) {
        if (existingTerm.meta === undefined) {
            existingTerm.meta = composingTerm.meta;
        }
        else {
            Object.assign(existingTerm.meta, composingTerm.meta);
        }
    }
    return { type: "success" };
}
//# sourceMappingURL=compose.js.map